import React, { Fragment, useState } from "react";

import Paragraph from "@common/components/Paragraph/Paragraph";
import { Attachment, SupportedFileTypes } from "@common/models";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { resolveFileType } from "@common/util/files";
import { AttachmentButtons } from "@main/pages/shared/components/Attachments/AttachmentButtons/AttachmentButtons";
import * as Icon from "@main/pages/shared/components/Attachments/icons";
import * as Styled from "@main/pages/shared/components/Attachments/UnsupportedAttachment/UnsupportedAttachment.styles";
import { CSSProperties } from "styled-components";

interface Props {
  attachment: Attachment;
  style?: CSSProperties;
  onRemoveClick?: (attachment: Attachment) => void;
}

export const UnsupportedAttachment = ({ attachment, style, onRemoveClick }: Props) => {
  const [hover, setHover] = useState(false);

  if (!attachment.url) return <Fragment />;

  const url = new URL(attachment.url);

  const filename: string = decodeURIComponent(url.pathname.split("/").pop()?.trim() || "Unknown file name");

  const attachmentType: SupportedFileTypes | undefined = attachment?.type
    ? attachment.type
    : resolveFileType(attachment.url);
  const attachmentTitle: string = attachment.name ? attachment.name : getTitle(filename, attachmentType);

  return (
    <Styled.AttachmentContainer
      style={style}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Styled.Title $alignItems="flex-start">
        <Flex $alignItems="flex-start">{getTypeIcon(attachmentType)}</Flex>
        <Paragraph
          fontSize={14}
          lineHeight={18}
          color={vars.colors.grey30}
        >
          {attachmentTitle}
        </Paragraph>
      </Styled.Title>

      {hover && (
        <AttachmentButtons
          attachment={attachment}
          onRemoveClick={onRemoveClick}
        />
      )}

      {attachment.text && (
        <Styled.AttachmentText $longTitle={filename.length > 32}>
          <Paragraph
            fontSize={14}
            lineHeight={18}
            color={vars.colors.grey70}
          >
            {attachment.text}
          </Paragraph>
        </Styled.AttachmentText>
      )}
    </Styled.AttachmentContainer>
  );
};

const getTypeIcon = (type?: SupportedFileTypes) => {
  switch (type) {
    case SupportedFileTypes.DOC:
      return <Icon.DocxIcon />;
    case SupportedFileTypes.PDF:
      return <Icon.PdfIcon />;
    case SupportedFileTypes.PPT:
      return <Icon.PowerpointIcon />;
    case SupportedFileTypes.TXT:
      return <Icon.LogTxtIcon />;
    case SupportedFileTypes.XLS:
      return <Icon.SheetsIcon />;
    default:
      return <Icon.AttachmentIcon />;
  }
};

const getTitle = (filename: string, type?: SupportedFileTypes) => {
  const splitFileName = filename.split(".");

  const ext = splitFileName[splitFileName.length - 1];

  const name = filename.slice(0, -ext.length - 1);

  if (type === SupportedFileTypes.TXT) {
    return name.length > 50 ? `${name.slice(0, 50)}....${ext}` : filename;
  }
  return name.length > 55 ? `${name.slice(0, 55)}....${ext}` : filename;
};
